import type { Components, Theme } from "@mui/material/styles";

import {
  borderWidth,
  color,
  effect,
  fluidFontSize,
  opacity,
  radius,
  spacing,
} from "@app/theme/weaveTokens.ts";
import { typographyVariant } from "@app/theme/typography.ts";

type ThemeComponents = Components<Omit<Theme, "components">>;

/**
 * Restores the Digital HIG Table look: the bordered container with a shadow
 * and large radius, bold header labels, the header divider, row dividers and
 * the clickable-row interaction styles.
 *
 * DHIG's `highlighted` row states (indigo tints) are not carried over: no table
 * in the app passes `highlighted`, and Weave has no tokens for them
 * (DEVX-11164).
 *
 * Every slot here is a plain object, never a function. Weave's slots for these
 * components are objects (`MuiTableHead.root` holds the sort cell cursor,
 * hover and focus rules and the select-all padding), and a function slot would
 * replace them instead of merging into them.
 */
export const tableOverrides: ThemeComponents = {
  MuiTable: {
    styleOverrides: {
      root: {
        "&.DhigTable--verticalAlignment--center tbody tr td": {
          verticalAlign: "middle",
        },
        "&.DhigTable--verticalAlignment--top tbody tr td": {
          verticalAlign: "top",
        },
        "&.DhigTable--zebra": {
          "& tbody tr:nth-of-type(odd)": {
            backgroundColor: color.panel,
          },
          "& tbody tr:hover": {
            backgroundColor: color.rowHoverZebra,
          },
        },
        "& tbody tr:last-child": { borderBottom: "none" },
        "&.DhigTable--bordered": {
          background: color.surface,
          boxShadow: effect.container,
          borderRadius: radius.large,
          "& tbody tr td:last-of-type": {
            padding: `${spacing.m} ${spacing.s} ${spacing.m} 0`,
            width: spacing.l,
            textAlign: "right",
            "& svg": { fontSize: fluidFontSize[5] },
          },
          '& tbody tr td[class*="MuiTableCell-sizeSmall"]:last-of-type': {
            width: spacing.px20,
            "& svg": { fontSize: fluidFontSize[4] },
          },
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      // DHIG's TableRow does not pass `enableInteractions` on to MUI. It adds
      // the class `DhigTableRow--enableInteractions` (default true), so the
      // interaction styles hang off that class. Weave keys the same styles on
      // the prop, which never reaches MUI, so without this rows lose their
      // pointer, hover, pressed and keyboard focus styles. Scoping to the class
      // keeps header rows and `enableInteractions={false}` rows inert. Values
      // are DHIG themeLight's own `MuiTableRow` override.
      root: {
        // Weave's row styles are `variants` keyed on the `enableInteractions`
        // prop, and its `defaultProps` set that prop to true on every row, so
        // left in they make header rows and `enableInteractions={false}` rows
        // clickable-looking. DHIG rows use classes instead (below).
        variants: [],
        position: "relative",
        border: 0,
        borderBottom: `${borderWidth.thin} solid ${color.rowDivider}`,
        "&.DhigTableRow--enableInteractions": {
          cursor: "pointer",
          "&:hover": {
            backgroundColor: color.rowHover,
            '& [class*="MuiTableCell-isEditing"] [class*="MuiInputBase-root"].Mui-disabled':
              { background: color.fadeTransparent },
          },
          "&:active": { backgroundColor: color.rowPressed },
          "&:focus-visible": {
            boxShadow: `inset ${effect.focusRingThin}`,
          },
          '&:hover:has(label[class*="MuiFormControlLabel-root"]:hover)': {
            boxShadow: "none",
            background: "initial",
          },
        },
        "&.Mui-selected": {
          backgroundColor: color.rowSelected,
          "&:hover": { backgroundColor: color.rowSelected },
        },
        "&.Mui-selected.DhigTableRow--enableInteractions": {
          "&:hover": { backgroundColor: color.rowSelectedHover },
          "&:active": {
            backgroundColor: color.rowSelectedPressed,
          },
        },
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        "& tr": {
          borderBottom: `${borderWidth.thin} solid ${color.dividerStrong}`,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      // DHIG's cell focus ring is 1px, Weave's is 2px.
      root: {
        "&:focus-visible": {
          boxShadow: `0px 0px 0px 1px inset ${color.iris}`,
          outline: "none",
        },
      },
    },
  },
  MuiTableSortLabel: {
    styleOverrides: {
      root: {
        ...typographyVariant["headline-smaller"],
        color: color.ink,
        pointerEvents: "none",
        "&:hover, :focus": { color: color.ink },
        "&.Mui-active": { color: color.ink },
        "& svg[class*='MuiSvgIcon-root']": {
          fontSize: fluidFontSize[4],
          paddingLeft: "2px",
          opacity: opacity.hidden,
        },
        "&:hover svg[class*='MuiSvgIcon-root']": {
          opacity: opacity.disabled,
        },
        "&.Mui-active svg[class*='MuiSvgIcon-root']": {
          opacity: opacity.visible,
        },
      },
    },
  },
};
